import { useMemo } from 'react'

import { matchFilterArrayIncludes, matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'
import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { TeamModel, TeamRequestOptions } from '@domain-group/realhub-backend-api-types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['teams']

type TeamFilters = {
  string?: string,
  user_id?: number,
}

type UseTeamsOptions = {
  filters?: TeamFilters,
  performHttpRequests?: boolean,
  requestOptions?: TeamRequestOptions,
}

function useTeams(options: UseTeamsOptions) {
  const { filters = {}, requestOptions } = options
  const {
    string: filterString,
    user_id: filterUserId,
  } = filters

  const {
    updatedEntities: { teams: teamsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { teams } = useSelector(reduxState => reduxState.entities)

  const filteredTeams = useMemo(() => {
    const filtered = Object.values(teams).filter((team: TeamModel) => {
      const { id, title, user_ids } = team

      const matchId = id.toString() === filterString
      const matchTitle = matchFilterString(title, filterString)
      const matchString = matchId || matchTitle
      // @ts-expect-error TS2345
      const matchUserId = matchFilterArrayIncludes(user_ids, filterUserId)

      return matchString && matchUserId
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [teamsUpdatedAt, JSON.stringify(filters)])

  const filteredTeamsCount = filteredTeams.length
  const hasFilteredTeams = !!filteredTeamsCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredTeamsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingTeams } = useReduxAction(
    'teams',
    'loadTeams',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredTeams,
    filteredTeamsCount,
    hasFilteredTeams,
    loading: loadingTeams,
  }
}

export default useTeams
